<template>
  <v-container class="fill-height with-fancy-bg">
    <v-row
      align="center"
      class="fill-height"
      justify="center">
      <v-col>
        <v-card
          class="mx-auto"
          max-width="688px">
          <div class="right-language-switcher">
            <LanguageSwitcher />
          </div>
          <v-card-title class="text-center mt-10 auth-forms-top-title">
            <span class="text-primary">{{ $t("views.login.WelcomeTop") }}</span>
            <br />
            {{ $t("views.login.WelcomeBottom") }}
          </v-card-title>
          <v-card-subtitle
            v-if="errorMessage"
            class="auth-forms-sub-title text-center text-red">
            {{ $t("actions.error-message") }}
            {{ $t("actions.error-responses." + errorMessage) }}
          </v-card-subtitle>
          <v-card-subtitle
            v-else
            class="auth-forms-sub-title text-center">
            {{ $t("views.login.LoginInToStart") }}
          </v-card-subtitle>

          <v-card-text>
            <v-form
              v-model="valid"
              style="height: 300px; overflow-x: hidden">
              <v-text-field
                id="login-email"
                data-cy="login-email"
                v-model="email"
                autocomplete="email"
                class="my-2"
                clearable
                density="default"
                :label="$t('entities.user.fields.email')"
                :persistent-placeholder="false"
                :rules="emailRules"
                type="email"
                @keypress.enter="submit" />
              <v-text-field
                id="login-password"
                data-cy="login-password"
                v-model="password"
                :append-inner-icon="!showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                autocomplete="current-password"
                clearable
                density="default"
                :label="$t('entities.user.fields.password')"
                :persistent-placeholder="false"
                :rules="passwordRules"
                :type="showPassword ? 'input' : 'password'"
                @click:append-inner="showPassword = !showPassword"
                @keypress.enter="submit" />

              <v-row>
                <v-col class="text-left">
                  <v-checkbox
                    v-model="remember"
                    class="mb-4"
                    density="default"
                    :label="$t('views.login.RememberMe')" />
                </v-col>
              </v-row>

              <v-row class="mt-n10">
                <v-col class="text-center">
                  <v-btn
                    id="login-submit"
                    block
                    class="text-none text-subtitle-1"
                    color="primary"
                    :disabled="!valid"
                    prepend-icon="mdi-check"
                    rounded="xl"
                    size="large"
                    variant="flat"
                    data-cy="login-submit"
                    @click="submit">
                    {{ $t("actions.submit") }}
                    <template v-if="!loginButtonEnabled">
                      <v-progress-circular
                        color="primary"
                        indeterminate></v-progress-circular>
                    </template>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col class="text-center">
                  <router-link
                    class="mr-4 auth-forms-sub-title"
                    data-cy="register-link"
                    :to="{ name: 'signup' }">
                    {{ $t("views.login.Register") }}
                  </router-link>
                  <router-link
                    class="auth-forms-sub-title"
                    data-cy="forgot-password-link"
                    :to="{ name: 'reset-password' }">
                    {{ $t("views.login.ForgotPassword") }}
                  </router-link>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref } from "vue";
import { useTranslation } from "i18next-vue";

import LanguageSwitcher from "../../components/tools/LanguageSwitcher.vue";
import { useUserRules } from "../../validations/user.js";
import { useAuthStore } from "@/stores/authStore";

const { t } = useTranslation();
const authStore = useAuthStore();

const valid = ref(true);
const loginButtonEnabled = ref(true);

const remember = ref(false);
const email = ref("");
const password = ref("");
const showPassword = ref("");
const errorMessage = ref(null);

const { emailRules, passwordRules } = useUserRules(t);

function submit() {
  loginButtonEnabled.value = false;
  errorMessage.value = null;

  authStore
    .login(email.value, password.value, remember.value)
    .catch((error) => {
      errorMessage.value = error;
    })
    .finally(() => {
      loginButtonEnabled.value = true;
    });

  return;
}
</script>
